import {
  CometUserIdentityVerificationCategory as Category,
  CometUserIdentityVerificationCohort as Cohort,
  CometUserIdentityVerificationDisplayStatus as Status,
} from '@zg-rentals/ts-rental-manager-api-client';

import type { IdentityVerificationEntry, IdentityVerificationRestrictedAction } from '../types/identityVerification';

const DAY_MS = 24 * 60 * 60 * 1000;

const ALL_ACTIONS: Array<IdentityVerificationRestrictedAction> = [
  'PUBLISH_LISTING',
  'INVITE_TO_APPLY',
  'SETUP_PAYMENTS',
];

export const IDENTITY_VERIFICATION_MOCK_OFF = 'off';

const MOCKS = {
  grace: (now) => ({
    status: Status.NOT_STARTED,
    enrollment: { cohort: Cohort.EXISTING, gracePeriodEndsAt: now + 60 * DAY_MS },
    restrictedActions: [],
  }),
  'grace-ending': (now) => ({
    status: Status.NOT_STARTED,
    enrollment: { cohort: Cohort.EXISTING, gracePeriodEndsAt: now + 5 * DAY_MS },
    restrictedActions: [],
  }),
  'enforced-new': (now) => ({
    status: Status.NOT_STARTED,
    enrollment: { cohort: Cohort.NEW, gracePeriodEndsAt: now - DAY_MS },
    restrictedActions: ALL_ACTIONS,
  }),
  'enforced-existing': (now) => ({
    status: Status.NOT_STARTED,
    enrollment: { cohort: Cohort.EXISTING, gracePeriodEndsAt: now - DAY_MS },
    restrictedActions: ['PUBLISH_LISTING'],
  }),
  'in-progress': (now) => ({
    status: Status.IN_PROGRESS,
    enrollment: { cohort: Cohort.NEW, gracePeriodEndsAt: now - DAY_MS },
    restrictedActions: ALL_ACTIONS,
  }),
  'needs-review': (now) => ({
    status: Status.NEEDS_REVIEW,
    enrollment: { cohort: Cohort.NEW, gracePeriodEndsAt: now - DAY_MS },
    restrictedActions: ALL_ACTIONS,
  }),
  failed: (now) => ({
    status: Status.FAILED,
    enrollment: { cohort: Cohort.NEW, gracePeriodEndsAt: now - DAY_MS },
    restrictedActions: ALL_ACTIONS,
  }),
  expired: (now) => ({
    status: Status.EXPIRED,
    expiresAt: now - DAY_MS,
    enrollment: { cohort: Cohort.EXISTING, gracePeriodEndsAt: now - 400 * DAY_MS },
    restrictedActions: ['PUBLISH_LISTING'],
  }),
  'renewal-due': (now) => ({
    status: Status.VERIFIED,
    expiresAt: now + 20 * DAY_MS,
    enrollment: { cohort: Cohort.EXISTING, gracePeriodEndsAt: now - 345 * DAY_MS },
    restrictedActions: [],
  }),
  verified: (now) => ({
    status: Status.VERIFIED,
    expiresAt: now + 300 * DAY_MS,
    enrollment: { cohort: Cohort.EXISTING, gracePeriodEndsAt: now - 65 * DAY_MS },
    restrictedActions: [],
  }),
} satisfies Record<string, (now: number) => Omit<IdentityVerificationEntry, 'category'>>;

export type IdentityVerificationMockState = keyof typeof MOCKS;

export const IDENTITY_VERIFICATION_MOCK_STATES = Object.keys(MOCKS) as Array<IdentityVerificationMockState>;

export const isIdentityVerificationMockState = (value: string | undefined): value is IdentityVerificationMockState =>
  value !== undefined && Object.hasOwn(MOCKS, value);

export const getIdentityVerificationMock = (
  state: IdentityVerificationMockState,
  now: number,
): IdentityVerificationEntry => ({ category: Category.DOCUMENT_BIOMETRIC, ...MOCKS[state](now) });
